import { useCallback, useRef, useState, type ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export type ConfirmOptions = {
  title: string;
  /** Nói rõ CÁI GÌ sắp mất, không chỉ "hành động không thể hoàn tác". */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Mặc định `true` — hầu hết chỗ dùng là xoá. Đặt `false` cho việc không phá dữ liệu. */
  destructive?: boolean;
};

const DEFAULTS = {
  confirmLabel: "Xoá",
  cancelLabel: "Giữ lại",
  destructive: true,
} as const;

/**
 * Hộp thoại xác nhận DÙNG CHUNG cho mọi thao tác xoá.
 *
 * 🔴 **Thay cho `window.confirm`, không phải bọc thêm nó.** `window.confirm` chặn
 * cả luồng JS, không theo được theme/ngôn ngữ của app, và trên iOS Safari trong
 * WebView (Facebook/Zalo in-app browser — một phần đáng kể lưu lượng của site)
 * nó có thể bị chặn hẳn: khi ấy hàm trả `false` trong im lặng và người dùng bấm
 * xoá mà **không có gì xảy ra**, hoặc tệ hơn, trình duyệt cho qua luôn mà không
 * hỏi. Cả hai đều không có lỗi nào hiện ra.
 *
 * Dùng theo lối mệnh lệnh để thay một dòng `if (window.confirm(...))` thành một
 * dòng `if (await confirm({...}))`:
 *
 * ```tsx
 * const { confirm, confirmDialog } = useConfirm();
 * // ...
 * <button onClick={async () => {
 *   if (await confirm({ title: "Xoá phiên này?" })) del.mutate(id);
 * }} />
 * {confirmDialog}
 * ```
 *
 * ⚠️ **`confirmDialog` phải được render** — quên nó thì `confirm()` trả về một
 * promise không bao giờ resolve và nút xoá lặng lẽ không làm gì.
 */
export function useConfirm() {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  // Giữ resolver ở ref: đặt vào state thì mỗi lần mở hộp thoại là một lần render
  // thừa, và resolver cũ có thể bị đọc lại từ closure của render trước.
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    // Mở đè lên một hộp thoại đang chờ: đóng sổ cái cũ bằng `false` để bên gọi
    // trước không treo mãi.
    resolverRef.current?.(false);
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const open = options !== null;
  const confirmLabel = options?.confirmLabel ?? DEFAULTS.confirmLabel;
  const cancelLabel = options?.cancelLabel ?? DEFAULTS.cancelLabel;
  const destructive = options?.destructive ?? DEFAULTS.destructive;

  const confirmDialog = (
    <AlertDialog
      open={open}
      // Bấm ra ngoài / Esc → coi như huỷ. Không nối vào `settle` là promise treo.
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
    >
      <AlertDialogContent className="w-[calc(100vw-1.5rem)] rounded-2xl sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{options?.title}</AlertDialogTitle>
          {options?.description ? (
            <AlertDialogDescription>{options.description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => settle(true)}
            className={cn(
              destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, confirmDialog };
}
